import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axiosInstance from "../../utils/axiosInstance";
import apiConfig from "../../config/appConfig";
import SalesforceConfigForm from "./SalesforceConfigForm"; // Form component for Create/Edit
import { format } from "date-fns";
import { DATE_FORMAT_AM_PM } from "../../utils/constants";

const SalesforceConfigDataTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0); // Total rows count
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid uses 0-based indexing
    pageSize: 10, // Default rows per page
  });
  const [formOpen, setFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [sortModel, setSortModel] = useState([
    { field: "modifiedDate", sort: "desc" },
  ]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        apiConfig.endpoints.salesforceConfig.paginated,
        {
          params: {
            page: paginationModel.page + 1, // Convert 0-based to 1-based indexing for backend
            limit: paginationModel.pageSize,
            search: searchText,
            sortField: sortModel[0]?.field || "modifiedDate",
            sortOrder: sortModel[0]?.sort || "desc",
          },
        },
      );
      const { salesforceConfigs, totalPages } = response.data;
      setRows(salesforceConfigs);
      setTotalRows(totalPages * paginationModel.pageSize); // Update total rows count
    } catch (error) {
      console.error("Error fetching SalesforceConfigs:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch configurations.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleMenuOpen = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleOpenDeleteDialog = (row) => {
    setConfigToDelete(row);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleCopyJBURL = (row) => {
    const hostname = window.location.origin; // Get the current hostname
    const url = `${hostname}/viber-jb-activity/${row.uuid}`; // Construct the URL

    // Copy the URL to the clipboard
    navigator.clipboard.writeText(url).then(() => {
      setSnackbar({
        open: true,
        message: "JB URL copied to clipboard!",
        severity: "success",
      });
    }).catch((error) => {
      console.error("Failed to copy JB URL:", error);
      setSnackbar({
        open: true,
        message: "Failed to copy JB URL.",
        severity: "error",
      });
    });

    handleMenuClose(); // Close the menu after the action
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(
        apiConfig.endpoints.salesforceConfig.byId(
          configToDelete.uuid,
        ),
      );
      setSnackbar({
        open: true,
        message: "Configuration deleted successfully!",
        severity: "success",
      });
      fetchConfigs();
    } catch (error) {
      console.error("Error deleting SalesforceConfig:", error);
      setSnackbar({
        open: true,
        message: "Failed to delete configuration.",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setConfigToDelete(null);
    }
  };

  const handleOpenForm = (config = null) => {
    setSelectedConfig(config);
    setFormOpen(true);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleSortModelChange = (model) => setSortModel(model);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const columns = [
    { field: "salesforceConfigId", headerName: "ID", flex: 0.5 },
    { field: "name", headerName: "Name", flex: 1 },
    {
      field: "customer",
      headerName: "Customer Name",
      flex: 1,
      valueGetter: (customer) => {
        return customer.name || "Unknown";
      }
    },
    { field: "authURL", headerName: "Auth URL", flex: 1.5 },
    { field: "configType", headerName: "Config Type", flex: 1 },
    {
      field: "createDate",
      headerName: "Created Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "modifiedDate",
      headerName: "Modified Date",
      width: 200,
      valueFormatter: (date) => {
        if (!date) return ""; // Handle null or undefined params
        try {
          return format(new Date(date), DATE_FORMAT_AM_PM); // Safely format the date
        } catch (error) {
          console.error("Invalid date format:", date);
          return "Invalid Date"; // Fallback for invalid dates
        }
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const statusColors = {
          Active: "success",
          Inactive: "default",
        };

        return (
          <Chip
            label={params.row.status || "Unknown"} // Fallback to 'Unknown' if status is null or undefined
            color={statusColors[params.row.status] || "default"} // Use 'default' if the status is not in the mapping
            size="small"
            sx={{
              fontWeight: "bold",
              textTransform: "capitalize", // Make the label look cleaner
              borderRadius: 1, // Less rounded corners
              padding: "0 8px", // Adjust padding for better shape
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="more"
            aria-controls="actions-menu"
            aria-haspopup="true"
            onClick={(event) => handleMenuOpen(event, params.row)}
          >
            <MoreVertIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h4">Salesforce Configurations</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Search by name"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
          >
            Add Configuration
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: '70vh',
            borderRadius: 2,
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
            width: '100%'
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            paginationMode="server"
            pageSizeOptions={[10, 25, 50, 100]}
            rowsPerPageOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={(newModel) => {
              console.log("Pagination model changed:", newModel); // Debugging
              setPaginationModel(newModel); // Update pagination state
            }}
            rowCount={totalRows}
            getRowId={(row) => row.uuid}
            disableSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
          />
        </Box>
      )}

      <Menu
        id="actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleOpenForm(menuRow)}>Edit</MenuItem>
        <MenuItem onClick={() => handleOpenDeleteDialog(menuRow)}>Delete</MenuItem>
        <MenuItem onClick={() => handleCopyJBURL(menuRow)}>Copy JB URL</MenuItem>
      </Menu>

      <SalesforceConfigForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        initialValues={selectedConfig || {}}
        refreshConfigs={fetchConfigs}
        setSnackbar={setSnackbar}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete{" "}
          <strong>{configToDelete?.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesforceConfigDataTable;
